// Main entry point for terms of service page
import '../../styles/main.css';
import '../../styles/components.css';
import '../../styles/document-legal-pages.css';
import { initSentry } from '../../shared/sentry-config.ts';
import * as Sentry from '@sentry/react';
import { trackPageView } from '../../shared/services/analyticsService.ts';
import { Header } from '../../components/landing/header.js';
import { redirectWithLanguage, getCurrentLanguage } from '../../shared/utils/urlUtils.ts';
import i18n from '../../shared/i18n/config.ts';

// Initialize Sentry before other code
initSentry('web-terms', 'terms');

// Error handling with Sentry
window.addEventListener('error', (e) => {
  console.error('Global error:', e.error);
  Sentry.captureException(e.error, {
    tags: {
      errorType: 'global',
    },
  });
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled promise rejection:', e.reason);
  Sentry.captureException(e.reason, {
    tags: {
      errorType: 'unhandledRejection',
    },
  });
});

/**
 * Get translation by key
 */
function t(key, options = {}) {
  return i18n.t(key, options);
}

/**
 * Update all text on page with data-i18n attributes
 */
function updatePageText() {
  // Update elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n');
    if (key) {
      const translation = t(key);
      if (translation !== key) {
        element.textContent = translation;
      }
    }
  });

  // Show translation disclaimer only for non-English languages
  const lang = getCurrentLanguage();
  const disclaimerElement = document.querySelector('[data-i18n="terms.translationDisclaimer"]');
  if (disclaimerElement) {
    if (lang !== 'en') {
      disclaimerElement.style.display = 'block';
    } else {
      disclaimerElement.style.display = 'none';
    }
  }

  // Update page title
  document.title = t('terms.title') + ' - Gantty';

  // Update HTML lang attribute
  document.documentElement.lang = lang;
}

/**
 * Populate list elements from translation arrays
 */
function populateLists() {
  // Account items
  const accountItems = t('terms.account.items', { returnObjects: true });
  if (Array.isArray(accountItems) && accountItems.length > 0) {
    const list = document.getElementById('accountItems');
    if (list) {
      list.innerHTML = accountItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Use License items
  const useLicenseItems = t('terms.useLicense.items', { returnObjects: true });
  if (Array.isArray(useLicenseItems) && useLicenseItems.length > 0) {
    const list = document.getElementById('useLicenseItems');
    if (list) {
      list.innerHTML = useLicenseItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // User Content Responsibility items
  const userContentResponsibilityItems = t('terms.userContent.responsibility.items', { returnObjects: true });
  if (Array.isArray(userContentResponsibilityItems) && userContentResponsibilityItems.length > 0) {
    const list = document.getElementById('userContentResponsibilityItems');
    if (list) {
      list.innerHTML = userContentResponsibilityItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Prohibited Uses items
  const prohibitedUsesItems = t('terms.prohibitedUses.items', { returnObjects: true });
  if (Array.isArray(prohibitedUsesItems) && prohibitedUsesItems.length > 0) {
    const list = document.getElementById('prohibitedUsesItems');
    if (list) {
      list.innerHTML = prohibitedUsesItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Import Risks items
  const importRisksItems = t('terms.excelPowerPoint.importRisks.items', { returnObjects: true });
  if (Array.isArray(importRisksItems) && importRisksItems.length > 0) {
    const list = document.getElementById('importRisksItems');
    if (list) {
      list.innerHTML = importRisksItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Machine Registration items
  const machineRegistrationItems = t('terms.machineRegistration.items', { returnObjects: true });
  if (Array.isArray(machineRegistrationItems) && machineRegistrationItems.length > 0) {
    const list = document.getElementById('machineRegistrationItems');
    if (list) {
      list.innerHTML = machineRegistrationItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Administrator items
  const administratorItems = t('terms.teamFeatures.administrator.items', { returnObjects: true });
  if (Array.isArray(administratorItems) && administratorItems.length > 0) {
    const list = document.getElementById('administratorItems');
    if (list) {
      list.innerHTML = administratorItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // No Guarantees items
  const noGuaranteesItems = t('terms.disclaimers.noGuarantees.items', { returnObjects: true });
  if (Array.isArray(noGuaranteesItems) && noGuaranteesItems.length > 0) {
    const list = document.getElementById('noGuaranteesItems');
    if (list) {
      list.innerHTML = noGuaranteesItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Indemnification items
  const indemnificationItems = t('terms.indemnification.items', { returnObjects: true });
  if (Array.isArray(indemnificationItems) && indemnificationItems.length > 0) {
    const list = document.getElementById('indemnificationItems');
    if (list) {
      list.innerHTML = indemnificationItems.map(item => `<li>${item}</li>`).join('');
    }
  }

  // Termination by Gantty items
  const terminationByGanttyItems = t('terms.termination.byGantty.items', { returnObjects: true });
  if (Array.isArray(terminationByGanttyItems) && terminationByGanttyItems.length > 0) {
    const list = document.getElementById('terminationByGanttyItems');
    if (list) {
      list.innerHTML = terminationByGanttyItems.map(item => `<li>${item}</li>`).join('');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  // Get language from URL or detect
  const lang = getCurrentLanguage();
  
  // Store language in localStorage for consistency
  localStorage.setItem('gantty_language', lang);
  
  // Set HTML lang attribute
  document.documentElement.lang = lang;
  
  // Change i18next language
  await i18n.changeLanguage(lang);
  
  // Update page title
  document.title = t('terms.title') + ' - Gantty';
  
  // Track page view
  trackPageView('Terms of Service');
  
  // Initialize header
  const header = new Header();
  
  // Update page text with translations
  updatePageText();
  populateLists();
  
  // Update header language display after i18n is ready (with small delay to ensure DOM is ready)
  setTimeout(() => {
    if (header && typeof header.populateLanguageDropdown === 'function') {
      header.populateLanguageDropdown();
    }
  }, 100);
  
  // Listen for language changes
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
    document.title = t('terms.title') + ' - Gantty';
    updatePageText();
    populateLists();
    // Update header language display when language changes
    if (header && typeof header.populateLanguageDropdown === 'function') {
      header.populateLanguageDropdown();
    }
  });
  
  // Redirect to login page on login button click
  document.getElementById('loginBtn')?.addEventListener('click', async () => {
    redirectWithLanguage('/login');
  });
});


